import { Bound } from "lib/@components/binding/Bound"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { Alert, Button, Container, Grid, Typography } from "@mui/material"
import { ScheduleItemCompleteList } from "routes/schedule/components/schedule-item-complete-list"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { findInChildren } from "library/tree/index.mjs"
import { useRef } from "react"
import { CurrentSkills } from "routes/@lib/currentSkills"
import { Secure } from "lib/authorization/secure"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useReferenceState } from "lib/@hooks/use-reference"
import { FitInRemainingSpace } from "lib/@components/fit-in-remaining-space"
import { If } from "lib/@components/switch"
import { scheduleSearchFilter } from "routes/schedule/lib/search-filter.js"

export function FoldersAndSchedules() {
    const { facility, facilityGroup, select } = useBoundContext()
    const [viewAll, setViewAll] = useReferenceState("showAllSchedules", false)
    const [search] = useSearchParam("search", "")
    const target = facilityGroup ?? facility
    const removedSome = useRef(false)
    const groups = target.children.filter((s) => {
        const filteredBySkill = !!findInChildren(s, (s) => s.type !== "group" && !s.notInSkillSet).length
        removedSome.current ||= !filteredBySkill
        return s.type === "group" && (filteredBySkill || viewAll)
    })
    const schedules = target.children.filter(filter)
    const hasVisibleSubFolders = groups.some(hasVisibleScheduleItem)
    return (
        <Container maxWidth="lg">
            <Bound inSelector={true}>
                <FitInRemainingSpace
                    setHeight={true}
                    leaveSpace={12}
                    data-cy="folders-and-schedules-list"
                    sx={{ pr: 1, overflowX: "hidden" }}
                >
                    <Grid container spacing={1} justifyContent="flex-start">
                        {removedSome.current && (
                            <Secure hide demand="see-all-schedules">
                                <Alert severity="warning" sx={{ width: 1, mt: 5, mb: 2 }}>
                                    {!viewAll && (
                                        <>
                                            <Typography gutterBottom component="div">
                                                Some schedules/folders have been hidden because they do not match your
                                                specified
                                                <CurrentSkills />
                                            </Typography>

                                            <Button
                                                sx={{ mt: 2, mb: 3 }}
                                                variant="contained"
                                                color="primary"
                                                onClick={() => setViewAll(true)}
                                            >
                                                See all schedules
                                            </Button>
                                        </>
                                    )}
                                    {viewAll && (
                                        <>
                                            <Typography gutterBottom component="div">
                                                Some schedules or folders are being shown that do not match your
                                                specified
                                                <CurrentSkills />
                                            </Typography>

                                            <Button
                                                sx={{ mt: 2, mb: 3 }}
                                                variant="contained"
                                                color="primary"
                                                onClick={() => setViewAll(false)}
                                            >
                                                Hide Unmatched Schedules
                                            </Button>
                                        </>
                                    )}
                                </Alert>
                            </Secure>
                        )}
                        <ScheduleItemCompleteList items={groups} select={select} />
                        <Grid item xs={12}>
                            <SearchBoxUsingSearchParam autoFocus={false} />
                        </Grid>
                        <If value={!hasVisibleSubFolders}>
                            <Grid
                                item
                                xs={12}
                                aria-hidden="true"
                                data-cy="folders-and-schedules-empty-groups-spacer"
                                sx={{ height: 32 }}
                            />
                        </If>
                        <ScheduleItemCompleteList items={schedules} select={select} />
                    </Grid>
                </FitInRemainingSpace>
            </Bound>
        </Container>
    )

    function filter(s) {
        return (viewAll || !s.notInSkillSet) && s.type !== "group" && scheduleSearchFilter(s, search)
    }

    function hasVisibleScheduleItem(item) {
        return !!item.content
    }
}
